import { useState, useEffect, useCallback } from "react";
import {
    Card,
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Stack,
    CircularProgress,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { get, put, post } from "src/utils/networkUtils";
import { useSearchParams } from "react-router-dom";
import useWithUrlTenant from "src/hooks/useTenant";
import { getEnabledLangs } from "src/langs/langs";
import LanguageTabs from "src/components/language/LanguageTabs";

const LANGS = getEnabledLangs();

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`division-tabpanel-${index}`} aria-labelledby={`division-tab-${index}`}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

export default function EditDivisions() {
    const [divisions, setDivisions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [adding, setAdding] = useState(false);
    const [newDivision, setNewDivision] = useState({
        divisionName: "",
        divisionNameI18n: { en: "", fr: "" },
        divisionLevel: 1,
        xpRewards: 0,
    });
    const [activeAddLangTab, setActiveAddLangTab] = useState(0);
    const [activeEditLangTab, setActiveEditLangTab] = useState(0);
    const [searchParams] = useSearchParams();
    const tenantId = searchParams.get("tenantId");

    const withTenant = useWithUrlTenant();

    const fetchDivisions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await get(withTenant('/admin/divisions'));
            setDivisions(response.divisions ? response.divisions.reverse() : []);
        } catch (error) {
            console.error('Error fetching divisions:', error);
        } finally {
            setLoading(false);
        }
    }, [withTenant]);

    useEffect(() => {
        fetchDivisions();
    }, [fetchDivisions]);

    const handleDivisionChange = (divisionId, field) => (event) => {
        const updatedDivisions = divisions.map(division => {
            if (division.divisionId === divisionId) {
                return { ...division, [field]: event.target.value };
            }
            return division;
        });
        setDivisions(updatedDivisions);
    };

    const handleDivisionI18nChange = (divisionId, langKey) => (event) => {
        const updatedDivisions = divisions.map(division => {
            if (division.divisionId === divisionId) {
                return {
                    ...division,
                    divisionNameI18n: {
                        ...(division.divisionNameI18n || { en: "", fr: "" }),
                        [langKey]: event.target.value,
                    },
                    ...(LANGS.find(lang => lang.value === langKey) ? { divisionName: event.target.value } : {}),
                };
            }
            return division;
        });
        setDivisions(updatedDivisions);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const response = await put(withTenant('/admin/divisions'), { divisions });
            console.log('Divisions saved successfully:', response);
            // Optionally refresh the data
            await fetchDivisions();
        } catch (error) {
            console.error('Error saving divisions:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleAddDivision = async () => {
        try {
            setAdding(true);

            const body = {
                divisionName: newDivision.divisionNameI18n.en || newDivision.divisionName,
                divisionNameI18n: newDivision.divisionNameI18n,
                divisionLevel: parseInt(newDivision.divisionLevel, 10) || 1,
                xpRewards: parseInt(newDivision.xpRewards, 10) || 0,
                tenantId: tenantId ? parseInt(tenantId, 10) : undefined,
            };

            const response = await post(withTenant('/admin/divisions'), body);
            console.log('Division added successfully:', response);
            setOpenAddDialog(false);
            setNewDivision({ divisionName: "", divisionNameI18n: { en: "", fr: "" }, divisionLevel: 1, xpRewards: 0 });
            setActiveAddLangTab(0);
            await fetchDivisions();
        } catch (error) {
            console.error('Error adding division:', error);
        } finally {
            setAdding(false);
        }
    };

    return (
        <Box>
            {/* Add New Division Form */}
            <Card sx={{ mb: 3 }}>
                <Box sx={{ p: 3 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                            <Stack spacing={2} alignItems="center">
                                <CircularProgress size={40} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading divisions...
                                </Typography>
                            </Stack>
                        </Box>
                    ) : (
                        <Box>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                <LanguageTabs
                                    langs={LANGS}
                                    value={activeEditLangTab}
                                    onChange={(_, v) => setActiveEditLangTab(v)}
                                    ariaLabel="division name language tabs"
                                    idPrefix="division-tab"
                                />
                            </Box>
                            {divisions.map((division) => (
                                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }} key={division.divisionId}>
                                    <Grid item xs={1}>
                                        <Typography variant="subtitle1">
                                            {division.divisionId}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {LANGS.map((lang, i) => (
                                            <TabPanel key={lang.value} value={activeEditLangTab} index={i}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={`Division Name (${lang.label})`}
                                                    value={(division.divisionNameI18n || {})[lang.value] || ''}
                                                    onChange={handleDivisionI18nChange(division.divisionId, lang.value)}
                                                    placeholder={`Enter division name in ${lang.label}`}
                                                    required
                                                />
                                            </TabPanel>
                                        ))}
                                    </Grid>

                                    <Grid item xs={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Division Level"
                                            type="number"
                                            value={division.divisionLevel || ''}
                                            onChange={handleDivisionChange(division.divisionId, 'divisionLevel')}
                                            placeholder="Enter level"
                                            required
                                            inputProps={{ min: 1 }}
                                        />
                                    </Grid>

                                    <Grid item xs={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="XP Reward"
                                            type="number"
                                            value={division.xpRewards || ''}
                                            onChange={handleDivisionChange(division.divisionId, 'xpRewards')}
                                            placeholder="Enter XP reward"
                                            required
                                            inputProps={{ min: 0 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <img
                                                            src="/assets/icons/xp-level-badge.svg"
                                                            alt="XP Badge"
                                                            style={{ width: 20, height: 20 }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                </Grid>
                            ))}

                            {/* Save Button */}
                            <Grid container spacing={2} sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => setOpenAddDialog(true)}
                                            disabled={saving}
                                        >
                                            Add New Division
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSave}
                                            disabled={saving}
                                            startIcon={saving ? <CircularProgress size={16} /> : null}
                                        >
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Add Division Dialog */}
                            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
                                <DialogTitle>Add New Division</DialogTitle>
                                <DialogContent>
                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 360 }}>
                                        <Box>
                                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                                <LanguageTabs
                                                    langs={LANGS}
                                                    value={activeAddLangTab}
                                                    onChange={(_, v) => setActiveAddLangTab(v)}
                                                    ariaLabel="add division name language tabs"
                                                    idPrefix="add-division-tab"
                                                />
                                            </Box>
                                            {LANGS.map((lang, i) => (
                                                <TabPanel key={lang.value} value={activeAddLangTab} index={i}>
                                                    <TextField
                                                        label={`Division Name (${lang.label})`}
                                                        fullWidth
                                                        value={newDivision.divisionNameI18n[lang.value]}
                                                        onChange={(e) => setNewDivision((prev) => ({
                                                            ...prev,
                                                            divisionNameI18n: { ...prev.divisionNameI18n, [lang.value]: e.target.value },
                                                            ...(lang.value === 'en' ? { divisionName: e.target.value } : {}),
                                                        }))}
                                                    />
                                                </TabPanel>
                                            ))}
                                        </Box>
                                        <TextField
                                            label="Division Level"
                                            type="number"
                                            fullWidth
                                            value={newDivision.divisionLevel}
                                            onChange={(e) => setNewDivision((prev) => ({ ...prev, divisionLevel: e.target.value }))}
                                        />
                                        <TextField
                                            label="XP Rewards"
                                            type="number"
                                            fullWidth
                                            value={newDivision.xpRewards}
                                            onChange={(e) => setNewDivision((prev) => ({ ...prev, xpRewards: e.target.value }))}
                                        />
                                        <TextField
                                            label="Tenant ID"
                                            fullWidth
                                            value={tenantId || ''}
                                            disabled
                                        />
                                    </Box>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setOpenAddDialog(false)} disabled={adding}>Cancel</Button>
                                    <Button onClick={handleAddDivision} variant="contained" disabled={adding} startIcon={adding ? <CircularProgress size={16} /> : null}>
                                        {adding ? 'Adding...' : 'Add'}
                                    </Button>
                                </DialogActions>
                            </Dialog>

                        </Box>
                    )}
                </Box>
            </Card>


        </Box>
    );
}
